/** DA — show delivery addresses in Hindi (terms + Devanagari transliteration). */

const DEVANAGARI_RE = /[\u0900-\u097F]/;

/** Longest phrases first — cities, states, common NCR/locality names. */
const PHRASE_MAP = [
  ["Greater Noida West", "ग्रेटर नोएडा वेस्ट"],
  ["Greater Noida", "ग्रेटर नोएडा"],
  ["New Delhi", "नई दिल्ली"],
  ["Uttar Pradesh", "उत्तर प्रदेश"],
  ["Crossing Republik", "क्रॉसिंग रिपब्लिक"],
  ["Indirapuram", "इंदिरापुरम"],
  ["Vaishali", "वैशाली"],
  ["Kaushambi", "कौशाम्बी"],
  ["Ghaziabad", "गाज़ियाबाद"],
  ["Faridabad", "फरीदाबाद"],
  ["Gurugram", "गुरुग्राम"],
  ["Gurgaon", "गुरुग्राम"],
  ["Noida", "नोएडा"],
  ["Delhi", "दिल्ली"],
  ["Pune", "पुणे"],
  ["Mumbai", "मुंबई"],
  ["Bengaluru", "बेंगलुरु"],
  ["Bangalore", "बेंगलुरु"],
  ["Hyderabad", "हैदराबाद"],
  ["Chennai", "चेन्नई"],
  ["Kolkata", "कोलकाता"],
  ["India", "भारत"],
];

const WORD_MAP = {
  flat: "फ्लैट",
  flt: "फ्लैट",
  floor: "मंजिल",
  fl: "मंजिल",
  tower: "टावर",
  block: "ब्लॉक",
  blk: "ब्लॉक",
  wing: "विंग",
  society: "सोसायटी",
  soc: "सोसायटी",
  apartment: "अपार्टमेंट",
  apt: "अपार्टमेंट",
  road: "सड़क",
  rd: "सड़क",
  street: "गली",
  st: "गली",
  lane: "गली",
  ln: "गली",
  avenue: "मार्ग",
  ave: "मार्ग",
  marg: "मार्ग",
  nagar: "नगर",
  colony: "कॉलोनी",
  sector: "सेक्टर",
  sec: "सेक्टर",
  plot: "प्लॉट",
  house: "मकान",
  hno: "मकान नं",
  building: "इमारत",
  bldg: "इमारत",
  landmark: "निशान",
  phase: "फेज",
  extension: "एक्सटेंशन",
  ext: "एक्सटेंशन",
  chowk: "चौक",
  gali: "गली",
  cross: "क्रॉस",
  main: "मुख्य",
  market: "बाज़ार",
  hospital: "अस्पताल",
  mall: "मॉल",
  school: "स्कूल",
  temple: "मंदिर",
  station: "स्टेशन",
  metro: "मेट्रो",
  gate: "गेट",
  near: "के पास",
  opp: "सामने",
  opposite: "सामने",
  behind: "पीछे",
  beside: "बगल में",
  next: "अगले",
  to: "को",
  the: "",
  and: "और",
  no: "नं",
  number: "नं",
  pincode: "पिनकोड",
  pin: "पिन",
  code: "कोड",
  delivery: "डिलीवरी",
  address: "पता",
  ground: "भूतल",
  basement: "तहखाना",
  shop: "दुकान",
  office: "कार्यालय",
  complex: "कॉम्प्लेक्स",
  enclave: "एन्क्लेव",
  heights: "हाइट्स",
  residency: "रेज़िडेंसी",
  park: "पार्क",
  garden: "गार्डन",
  green: "ग्रीन",
  greens: "ग्रीन्स",
  villa: "विला",
  villas: "विलास",
  township: "टाउनशिप",
  layout: "लेआउट",
  county: "काउंटी",
};

/** ITRANS-style — roman → Devanagari for leftover tokens (society/building names). */
const ITRANS = [
  ["aa", "आ"],
  ["ii", "ई"],
  ["ee", "ी"],
  ["uu", "ू"],
  ["oo", "ू"],
  ["ai", "ै"],
  ["au", "ौ"],
  ["kh", "ख"],
  ["gh", "घ"],
  ["ch", "च"],
  ["jh", "झ"],
  ["th", "थ"],
  ["dh", "ध"],
  ["ph", "फ"],
  ["bh", "भ"],
  ["sh", "श"],
  ["ng", "ङ"],
  ["ny", "ञ"],
  ["k", "क"],
  ["g", "ग"],
  ["c", "क"],
  ["j", "ज"],
  ["t", "ट"],
  ["d", "ड"],
  ["n", "न"],
  ["p", "प"],
  ["b", "ब"],
  ["m", "म"],
  ["y", "य"],
  ["r", "र"],
  ["l", "ल"],
  ["v", "व"],
  ["w", "व"],
  ["s", "स"],
  ["h", "ह"],
  ["f", "फ"],
  ["x", "क्स"],
  ["z", "ज़"],
  ["q", "क"],
  ["a", "अ"],
  ["i", "इ"],
  ["u", "उ"],
  ["e", "े"],
  ["o", "ो"],
];

const addressCache = new Map();

function hasDevanagari(text) {
  return DEVANAGARI_RE.test(text);
}

function transliterateToken(token) {
  if (!token || /^\d+$/.test(token)) return token;
  if (!/[A-Za-z]/.test(token)) return token;

  let lower = token.toLowerCase();
  if (WORD_MAP[lower] !== undefined) return WORD_MAP[lower] || "";

  let src = lower;
  let out = "";
  while (src.length) {
    let matched = false;
    for (const [from, to] of ITRANS) {
      if (src.startsWith(from)) {
        out += to;
        src = src.slice(from.length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      out += src[0];
      src = src.slice(1);
    }
  }

  // Title-case brand tokens: capitalize first Devanagari if original was capitalized
  if (/^[A-Z]/.test(token) && out) {
    return out;
  }
  return out || token;
}

function translatePhrases(text) {
  let out = text;
  for (const [en, hi] of PHRASE_MAP) {
    const re = new RegExp(en.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    out = out.replace(re, hi);
  }
  return out;
}

function translateWords(text) {
  return text.replace(/[A-Za-z]+(?:'[A-Za-z]+)?/g, (word) => {
    const mapped = WORD_MAP[word.toLowerCase()];
    if (mapped !== undefined) return mapped;
    if (/^[A-Z]{2,}$/.test(word)) {
      // Acronyms: ATS → एटीएस
      return word
        .split("")
        .map((ch) => transliterateToken(ch))
        .join("");
    }
    if (/[A-Za-z]/.test(word)) return transliterateToken(word);
    return word;
  });
}

/**
 * Format address for DA Hindi mode.
 * Keeps numbers, punctuation, and map URLs unchanged at call sites.
 */
export function formatDriverAddress(lang, address) {
  const raw = String(address || "").trim();
  if (!raw || lang !== "hi") return raw || "—";
  if (hasDevanagari(raw)) return raw;

  const cached = addressCache.get(raw);
  if (cached) return cached;

  let text = translatePhrases(raw);
  text = translateWords(text);
  text = text.replace(/\s{2,}/g, " ").replace(/\s+,/g, ",").trim();

  addressCache.set(raw, text);
  return text;
}
